'use client';

import Image from 'next/image';
import Link from 'next/link';

import TrustedPartners from "./TrustedPartners";
import { DigitalAiPower as Icon_DigitalAiPower } from "@/components/customer/icons/DigitalAiPower";
import { DigitalBoost as Icon_DigitalBoost } from "@/components/customer/icons/DigitalBoost";
import { DigitalDataDriven as Icon_DigitalDataDriven } from "@/components/customer/icons/DigitalDataDriven";
import { DigitalExpertise as Icon_DigitalExpertise } from "@/components/customer/icons/DigitalExpertise";
import { DigitalStrategic as Icon_DigitalStrategic } from "@/components/customer/icons/DigitalStrategic";
import { ReviewStar as Icon_ReviewStar } from "@/components/customer/icons/ReviewStar";
const SvgIcons = { DigitalAiPower: Icon_DigitalAiPower, DigitalBoost: Icon_DigitalBoost, DigitalDataDriven: Icon_DigitalDataDriven, DigitalExpertise: Icon_DigitalExpertise, DigitalStrategic: Icon_DigitalStrategic, ReviewStar: Icon_ReviewStar };
import GlobalPartnersCount from "@/components/GlobalPartnersCount";
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation,
  Autoplay,
  FreeMode,
} from 'swiper/modules';
import { Fragment, useEffect, useState, useRef } from 'react';
// Old testimonials component no longer used here — unified to CircularTestimonialView
// import Testimonials from './Testimonials';
import GoogleImg from '../images/G_image.webp';
import seoImage from '../images/seoImage.png';
import { useAOS } from '@/hooks/useAOS';
import TestiReviewCommonImages from './otherpages/TestiReviewCommonImages';
import FaqSection from './FaqSection';
import PricingSection from './PricingSection';
import { getImageUrl } from '@/lib/imageUrl';
import StatsSection from './StatusSection';
import StatsBand from './StatsBand';
import { MARKETING_STATS, MARKETING_UPDATED_AT } from '@/lib/marketingStats';

interface Heading {
  id: number;
  title: string;
  content: string;
}

const customStyles = `
  .swiper-button-prev, .swiper-button-next {
border-radius: 18px;
border: 1px solid #E4E9F0;
background: #FFF;
box-shadow: 0px 68px 19px 0px rgba(0, 9, 56, 0.00), 0px 43px 17px 0px rgba(0, 9, 56, 0.00), 0px 24px 15px 0px rgba(0, 9, 56, 0.01), 0px 11px 11px 0px rgba(0, 9, 56, 0.02), 0px 3px 6px 0px rgba(0, 9, 56, 0.02);
    width: 40px;
    height: 40px;
    font-size: 15px;
    padding:4px;
  }
  .swiper-button-prev::after, .swiper-button-next::after {
    font-size: 0px; /* Adjust arrow size */
  }
  .swiper-button-prev {
    left: 10px; /* Position the previous button */
  }
  .swiper-button-next {
    right: 10px; /* Position the next button */
  }
    .swiper-button-prev, .swiper-button-next{
    position: static; /* Override default position */}
  /* Ensure buttons are not overlapped by other elements */
  .swiper-container {
    position: relative;
    padding: 0 50px; /* Add padding to accommodate buttons */
  }
`;
const DigitalMarketingView = () => {
  const faqs = [
    {
      question: 'What is digital marketing and how does it work?',
      answer:
        'Digital marketing refers to all marketing efforts that use the internet or electronic devices. It works by promoting your products or services through channels like search engines, social media, email, and websites to reach potential customers, increase brand awareness, and drive sales.',
    },
    {
      question: 'How long does it take to see results from digital marketing?',
      answer:
        'It depends on the strategy used. SEO may take 3–6 months to show organic growth, while paid ads (like Google Ads or social media ads) can generate results in a few days. A consistent and optimized approach typically brings steady and scalable results over time.',
    },
    {
      question: 'What’s the difference between SEO and paid advertising (PPC)?',
      answer:
        'SEO focuses on improving your website’s visibility in organic search results, which takes time but offers long-term benefits. PPC (Pay-Per-Click) advertising places your brand at the top of search or social feeds immediately, but you pay for every click. A smart strategy often uses both.',
    },
    {
      question:
        'Can digital marketing really help local or small businesses grow?',
      answer:
        'Absolutely. With tools like local SEO, Google Business Profile optimization, social media marketing, and targeted ads, even small businesses can reach nearby customers, build trust, and increase footfall or online sales. Digital levels the playing field for businesses of all sizes.',
    },
    {
      question:
        'What makes Vilva Business different from other digital marketing agencies?',
      answer:
        'Vilva Business provides an integrated solution—combining web development, e-commerce setup, marketing, and automation. We don’t just run ads—we align every digital touchpoint for maximum ROI. Our agile team supports startups and SMEs with tailored, scalable strategies.',
    },
    {
      question:
        'How does Vilva Business create customized strategies for different',
      answer:
        'We start with in-depth market and competitor research, then build unique campaigns using the right mix of SEO, social media, content, and paid ads. Each plan is optimized for your audience, whether you’re in retail, services, education, or tech.',
    },
  ];

  useAOS();

  const headings: Heading[] = [
    {
      id: 1,
      title: 'Customer Persona Development',
      content:
        'Every successful campaign begins with a deep understanding of the target audience. We use the power of social media to analyze demographics, interests, and online behaviors, helping us create detailed customer personas. This insight allows us to tailor content and campaigns that resonate, ensuring your message reaches those who matter most.',
    },
    {
      id: 2,
      title: 'Content Calendar & Strategy',
      content: `A well-structured content calendar is the backbone of effective social media marketing. We plan and schedule posts that align with your brand's voice and objectives, ensuring consistent engagement. By blending evergreen content with trending topics, we keep your audience informed and engaged.`,
    },
    {
      id: 3,
      title: 'Paid Advertising Campaigns',
      content: `Organic reach is valuable, but paid advertising expands your brand's presence. We design targeted ad campaigns across platforms like Facebook, Instagram, and LinkedIn, focusing on demographics and user behavior to maximize ROI. Our strategies are data-driven, ensuring every penny spent contributes to your goals.`,
    },
    {
      id: 4,
      title: 'Analytics & Reporting',
      content: `Transparency and continuous improvement are at our core. We provide detailed analytics and reports, highlighting key performance indicators like engagement rates, click-through rates, and conversion metrics. These insights inform strategy adjustments, ensuring your social media efforts are always optimized.`,
    },
    {
      id: 5,
      title: 'E-Commerce Social Media Marketing',
      content: `For eCommerce brands, social media is more than just a platform—it's a powerful storefront. At Vilva Business, we create strategies that turn passive scrolling into active shopping. By combining eye-catching creatives, strategic product placement, we help you drive product discovery and boost direct sales.`,
    },
  ];
  const [activeHeading, setActiveHeading] = useState(headings[0].id);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHeading((prev) => {
        const currentIndex = headings.findIndex((h) => h.id === prev);
        const nextIndex = (currentIndex + 1) % headings.length;
        return headings[nextIndex].id;
      });
    }, 5000); // 5 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const handleHeadingClick = (id) => {
    setActiveHeading(id);
  };

  // Safely find the active heading with type assertion
  const activeHeadingData = headings.find((h) => h.id === activeHeading)!;

  // Add refs for Swiper navigation buttons (mobile)
  const emailPrevRef = useRef<HTMLDivElement | null>(null);
  const emailNextRef = useRef<HTMLDivElement | null>(null);

  // Add refs for SEO Swiper navigation buttons
  const seoPrevRef = useRef<HTMLButtonElement | null>(null);
  const seoNextRef = useRef<HTMLButtonElement | null>(null);
  const seoPrevRef2 = useRef<HTMLButtonElement | null>(null);
  const seoNextRef2 = useRef<HTMLButtonElement | null>(null);

  const [activeCard, setActiveCard] = useState<any>(null);
  const [isUserHovering, setIsUserHovering] = useState(false);

  // Card data
  const cards = [
    {
      icon: SvgIcons.DigitalStrategic,
      title: 'Customized Strategic Solutions',
      description:
        'We don’t believe in one-size-fits-all. Every campaign is custom-built based on your goals, audience, and industry to ensure maximum impact and ROI.',
    },
    {
      icon: SvgIcons.DigitalExpertise,
      title: '360° Digital Marketing Expertise',
      description:
        'From SEO and paid ads to social media, email marketing, and automation, we offer complete digital solutions under one roof, saving you time and effort.',
    },
    {
      icon: SvgIcons.DigitalDataDriven,
      title: 'Data-Driven Optimization',
      description:
        'We don’t guess - we analyze. Every campaign is continuously monitored and optimized using real-time data, ensuring your budget goes where it performs best. Better decisions, better results.',
    },
    {
      icon: SvgIcons.DigitalAiPower,
      title: 'AI-Powered Strategies',
      description:
        'We leverage the power of artificial intelligence to analyze behavior, predict trends, and automate decisions — delivering smarter, faster, and more efficient digital campaigns that outperform traditional methods.',
    },
    {
      icon: SvgIcons.DigitalBoost,
      title: 'Boost Traffic Effectively',
      description:
        'We design targeted campaigns that drive high-quality traffic to your business. Our data-driven strategies focus on maximizing conversion rates and delivering measurable growth.',
    },
  ];

  // Auto-hover effect
  useEffect(() => {
    if (isUserHovering) return; // Stop if user is hovering

    const interval = setInterval(() => {
      // Select a random card index
      const randomIndex = Math.floor(Math.random() * cards.length);
      setActiveCard(randomIndex);
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval); // Cleanup on unmount or when hovering
  }, [isUserHovering, cards.length]);

  const handleMouseEnter = (index) => {
    setIsUserHovering(true);
    setActiveCard(index);
  };

  const handleMouseLeave = () => {
    setIsUserHovering(false);
  };
  return (
    <>
      <section className='bg-[linear-gradient(180deg,_#FFF_85.76%,_#FFEFFF_100%)]'>
        <div className='container'>
          <div className='flex items-center justify-center pt-[100px]'>
            <div>
              <p className='mx-auto w-fit bg-gradient-to-r from-[#3E00B0] to-[#00ADEC] bg-clip-text text-center font-Schibsted_Grotesk text-[18px] font-[400] uppercase leading-[100%] tracking-[3.2px] text-transparent md:text-[20px]'>
                DIGITAL MARKETING
              </p>
              <div className='relative'>
                <h1 className='mx-auto my-4 w-full text-center font-NeueMontreal text-[42px] font-[400] leading-[120%] text-[#090D28] md:w-[700px] md:text-[62px] lg:w-[900px]'>
                  Turn{' '}
                  <span className='py-4 font-NeueMontreal text-[42px] font-[400] leading-[120%] text-[#3F92FF] md:text-[62px]'>
                    Clicks into Customers
                  </span>{' '}
                  Effortlessly with Vilva
                </h1>

                <div className='animate-wave5 absolute -left-1 bottom-0 m-0    p-0 sm:-left-7 md:-left-5 md:top-[22%]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    data-aos='fade-left'
                    data-aos-duration='1500'
                    className='relative left-[10px] top-[4px] ms-auto'
                    width='18'
                    height='17'
                    viewBox='0 0 18 17'
                    fill='none'
                  >
                    <g clipPath='url(#clip0_394_1159)'>
                      <path
                        d='M12.715 5.38758L10.9996 14.978C10.8327 15.9078 9.59422 16.1271 9.12001 15.3102L6.71644 11.1818L2.22878 9.5443C1.33959 9.22045 1.33834 7.96384 2.22409 7.63555L11.3614 4.25507C12.0999 3.9802 12.8541 4.61361 12.715 5.38758Z'
                        fill='#C37CEA'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_394_1159'>
                        <rect
                          width='12'
                          height='12'
                          fill='white'
                          transform='translate(0.54834 9.20337) rotate(-50.0809)'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p className='   m-0  inline-flex items-center justify-center rounded-[24px] bg-[#C37CEA] !py-[6px] px-3 font-LatinkaSemiBold text-[10px] font-[400]  text-white'>
                    {' '}
                    <span data-aos='fade-right' data-aos-duration='1000'>
                      Businesses
                    </span>{' '}
                  </p>
                </div>
                <div className='animate-wave absolute  -right-[40px]  bottom-24 m-0 p-0  sm:right-[5%] md:bottom-6'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    data-aos='fade-right'
                    data-aos-duration='1000'
                    className='relative right-3 top-1 me-auto'
                    width='17'
                    height='17'
                    viewBox='0 0 17 17'
                    fill='none'
                  >
                    <g clipPath='url(#clip0_394_1152)'>
                      <path
                        d='M4.7357 5.38758L6.45107 14.978C6.61795 15.9078 7.85646 16.1271 8.33067 15.3102L10.7342 11.1818L15.2219 9.5443C16.1111 9.22045 16.1123 7.96384 15.2266 7.63555L6.08925 4.25507C5.35082 3.9802 4.59657 4.61361 4.7357 5.38758Z'
                        fill='#FF7ACE'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_394_1152'>
                        <rect
                          width='12'
                          height='12'
                          fill='white'
                          transform='matrix(-0.641706 -0.766951 -0.766951 0.641706 16.9023 9.20337)'
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p className='m-0   inline-flex items-center  justify-center rounded-[24px] bg-[#FF7ACE] !py-[6px] px-3 font-Schibsted_Grotesk text-[10px] font-[400] text-white'>
                    <span data-aos='fade-left' data-aos-duration='1000'>
                      Customers
                    </span>
                  </p>
                </div>
              </div>

              <p className='text-center font-Schibsted_Grotesk   text-[18px] font-[400] leading-[120%] text-[#373844] md:text-[20px]'>
                We help you build real connections, drive sales, and grow a
                brand people remember.
              </p>
              <div className='flex items-center justify-center gap-6 py-6'>
                {/* <Link data-aos="fade-right"  data-aos-duration="1500" className='text-[#195AFF] whitespace-nowrap font-LatinkaSemiBold text-[18px] md:text-[20px] font-[400] leading-[120%] flex w-[180px] h-[52px] px-8 py-4 justify-center items-center rounded-[8px] border-[1px] border-[#6F9CFF] bg-[#EFF4FF]' href={'/book-demo'}>Book Free Demo</Link> */}
                <Link
                  data-aos='fade-left'
                  data-aos-duration='1500'
                  className='flex   h-[52px] items-center justify-center whitespace-nowrap rounded-[8px] bg-[#195AFF] px-8 py-4 font-Schibsted_Grotesk text-[18px] font-[400] leading-[120%] text-white md:text-[20px]'
                  href={'/book-demo'}
                >
                  Book Free Demo
                </Link>
              </div>

              <div className="relative w-full max-w-[800px] mx-auto">
                {/* Desktop image */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/digital-marketing/Digital%20marketing%20hero%20section%20image.webp")}
                  alt="Digital marketing services by Vilva Business"
                  width={800}
                  height={500}
                  className="hidden md:block w-full h-auto"
                  priority
                  decoding="async"
                />

                {/* Mobile image */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/digital-marketing/heroimage_mob_1.webp")}
                  alt="Digital marketing services by Vilva Business on mobile"
                  width={800}
                  height={500}
                  className="block md:hidden w-full h-auto"
                  priority
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustedPartners className="relative py-[20px] md:py-[30px]" />

      <StatsBand
        heading="Marketing That Actually Converts."
        stats={MARKETING_STATS}
        updatedAt={MARKETING_UPDATED_AT}
        // Picks up exactly where the hero's gradient stops (#FFEFFF) and settles
        // into #F1ECFA, the tint this page already uses behind its content
        // cards — so the band reads as part of the page rather than a panel
        // dropped onto it. The earlier cream was a card accent, not a section
        // colour, which is why it sat apart from everything around it.
        className="pt-[60px] md:pt-[100px] overflow-hidden relative bg-[linear-gradient(180deg,_#FFEFFF_0%,_#F1ECFA_100%)]"
        headingClassName="text-[#373844]"
        valueClassName="text-[#5A2AB2]"
        labelClassName="text-[#373844]"
        dividerClassName="border-[#DCD0F0]"
        updatedAtClassName="text-[#373844] opacity-60"
      />

      <section className='px-3 py-20 md:container md:px-0 md:py-[100px]'>
        <p
          className='
     
      mx-auto
      mb-6
      w-full
      text-center
      font-NeueMontreal text-[32px]
      font-normal
      leading-[120%]
      text-[#090D28] md:w-[700px] md:text-[48px] 
    '
          data-aos='fade-up'
          data-aos-duration='1500'
        >
          5 Reasons Why Choose Vilva for{' '}
          <span
            className='
        ml-2
        font-NeueMontreal
        text-[32px]
        font-normal
        leading-[120%] text-[#3F92FF]
        md:text-[48px]

      '
            data-aos='fade-up'
            data-aos-duration='2000'
          >
            Digital Marketing
          </span>
        </p>
        <p
          data-aos='fade-up'
          data-aos-duration='2500'
          className='
      mx-auto
      mb-[48px]
      mt-6
      w-full
      text-center
      font-Schibsted_Grotesk text-[16px]
      font-normal   leading-[140%]  text-[#373844] md:w-[800px] md:text-[18px]
    '
        >
          Vilva Business delivers full-service digital marketing crafted for
          startups, scale-ups, and bold brands. From SEO to automation, we drive
          visibility, leads, and ROI with data-backed, result-focused
          strategies.
        </p>

        <div className=' '>
          <div className='flex flex-wrap'>
            {cards.map((card, index) => (
              <div
                key={index}
                data-aos="fade-right"
                data-aos-duration="800"
                className={`p-3 ${index < 3 ? 'w-full md:w-6/12 lg:w-4/12' : 'w-full md:w-1/2'
                  }`}
              >
                <div
                  className={`h-full  rounded-[24px] p-[24px] transition-all duration-300 ease-in-out ${activeCard === index
                    ? 'bg-[#3F92FF]'
                    : 'bg-[#EDF5FF] hover:bg-[#3F92FF]'
                    } group`}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className='mb-[33px]'>
                    <card.icon
                      className={`transition-colors duration-300 ease-in-out ${activeCard === index
                        ? 'fill-white'
                        : 'group-hover:fill-white'
                        }`}
                    />
                  </div>
                  <p
                    className={`mb-4 text-start font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#3F92FF] transition-colors duration-300 ease-in-out md:text-[22px] ${activeCard === index
                      ? 'text-white'
                      : 'group-hover:text-white'
                      }`}
                  >
                    {card.title}
                  </p>
                  <p
                    className={`font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844] transition-colors duration-300 ease-in-out md:text-[18px] ${activeCard === index
                      ? 'text-white'
                      : 'group-hover:text-white'
                      }`}
                  >
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='px-2 py-20 md:container md:px-0 md:py-[100px]'>
        <h2 className='mb-12 w-full ps-2 font-NeueMontreal text-[32px] font-normal not-italic leading-[120%] text-[#090D28] md:w-[650px] md:ps-0 md:text-[40px]'>
          <span className='font-NeueMontreal text-[32px] font-normal not-italic leading-[120%] text-[#3F92FF] md:text-[40px]'>
            {' '}
            Grow Smarter With Expert{' '}
          </span>
          Digital Marketing Made to Get Real Results.
        </h2>

        <div className='relative rounded-[8px] border border-[#CBE0FA] bg-white p-[24px]'>
          {/* SEO Optimization heading with navigation buttons for first Swiper */}
          <div className='mb-6 flex items-center'>
            <h3 className='mr-2 whitespace-nowrap font-NeueMontreal text-[32px] font-semibold not-italic leading-[120%] text-[#090D28] md:text-[40px]'>
              SEO Optimization
            </h3>
            <div className='flex gap-2'>
              <button
                ref={seoPrevRef}
                type='button'
                aria-label="Previous slide"
                className='seo-swiper-prev relative z-50 flex items-center justify-center'
                style={{
                  borderRadius: '18px',
                  border: '1px solid #E4E9F0',
                  background: '#FFF',
                  boxShadow:
                    '0px 68px 19px 0px rgba(0, 9, 56, 0.00), 0px 43px 17px 0px rgba(0, 9, 56, 0.00), 0px 24px 15px 0px rgba(0, 9, 56, 0.01), 0px 11px 11px 0px rgba(0, 9, 56, 0.02), 0px 3px 6px 0px rgba(0, 9, 56, 0.02)',
                  width: '40px',
                  height: '40px',
                  padding: 0,
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M12.5 15L7.5 10L12.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
              <button
                ref={seoNextRef}
                aria-label="Next slide"
                type='button'
                className='seo-swiper-next relative z-50 flex items-center justify-center'
                style={{
                  borderRadius: '18px',
                  border: '1px solid #E4E9F0',
                  background: '#FFF',
                  boxShadow:
                    '0px 68px 19px 0px rgba(0, 9, 56, 0.00), 0px 43px 17px 0px rgba(0, 9, 56, 0.00), 0px 24px 15px 0px rgba(0, 9, 56, 0.01), 0px 11px 11px 0px rgba(0, 9, 56, 0.02), 0px 3px 6px 0px rgba(0, 9, 56, 0.02)',
                  width: '40px',
                  height: '40px',
                  padding: 0,
                  zIndex: 10000000,
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 15L12.5 10L7.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </div>
          </div>

          <Swiper
            className='container'
            modules={[Navigation, Autoplay]}
            autoplay={{
              delay: 3000, // Adjusted delay for smoother experience
              disableOnInteraction: false,
            }}
            speed={1000} // Adjusted speed for smoother transitions
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              prevEl: seoPrevRef.current,
              nextEl: seoNextRef.current,
            }}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 24,
              },
            }}
            onBeforeInit={(swiper) => {
              // Ensure navigation is set before initialization
              // @ts-ignore
              swiper.params.navigation.prevEl = seoPrevRef.current;
              // @ts-ignore
              swiper.params.navigation.nextEl = seoNextRef.current;
            }}
            onInit={(swiper) => {
              swiper.navigation.init();
              swiper.navigation.update();
            }}
          >
            <SwiperSlide>
              <div className="h-[400px] rounded-[24px] bg-[linear-gradient(180deg,_#3F92FF_0%,_#003882_100%)] p-[24px] flex items-center justify-center">
                {/* Desktop image */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/digital-marketing/seoImage.webp")}
                  alt="SEO Illustration"
                  width={800}
                  height={500}
                  className="hidden md:block w-full h-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />

                {/* Mobile image */}
                <Image
                  src={getImageUrl("/vilvabusiness/website/products/digital-marketing/seoimage_mob_1.webp")}
                  alt="SEO Illustration"
                  width={800}
                  height={500}
                  className="block md:hidden w-full h-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />

              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#E0ECFB] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#206AC7] md:text-[22px]'>
                  Technical SEO Audit
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Every great SEO strategy begins with a solid foundation. Our
                  technical SEO audit dives deep into your website's
                  infrastructure to identify and fix performance bottlenecks. We
                  analyze page speed, crawl errors, indexing issues, mobile
                  responsiveness, and overall site health. This ensures your
                  site is not only accessible to users but also fully optimized
                  for search engine crawlers. With the help of advanced search
                  engine optimization tools, we deliver a comprehensive
                  technical report that highlights action points and
                  improvements.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#F9F7E4] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#8F7500] md:text-[22px]'>
                  Tag Optimization
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Meta tags, title tags, header tags, and alt attributes may
                  seem small—but they carry massive SEO value. We fine-tune
                  these elements to align with your target search engine
                  optimization keywords and user intent. Effective tag
                  optimization boosts your on-page SEO and ensures your content
                  is both readable and discoverable by search engines like
                  Google. The result? Higher click-through rates and better
                  visibility across your web pages.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#F9E4F9] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#206AC7] md:text-[22px]'>
                  Keyword Research
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  We conduct in-depth search engine optimization keyword
                  research to discover the exact terms your target audience is
                  using. Our approach balances high-search-volume keywords with
                  niche, long-tail phrases to help you dominate both competitive
                  and targeted spaces. Using powerful SEO tools and trend
                  analysis, we develop a keyword strategy that guides every
                  piece of your website content, driving traffic that converts.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#E4F9E7] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#238A31] md:text-[22px]'>
                  Content Structure Optimization
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Search engines love well-structured content—and so do users.
                  We optimize your content layout to ensure logical flow, use of
                  headers (H1, H2, etc.), keyword placement, and internal
                  linking. This improves readability, increases engagement, and
                  supports better indexing. Whether it's service pages, blog
                  posts, or landing pages, our focus is on creating a structure
                  that satisfies both human users and search engine optimizers.
                </p>
              </div>
            </SwiperSlide>
          </Swiper>

          {/* Second Swiper with separate navigation buttons */}
          {/* <div className="flex items-center mb-6 mt-6">
      <div className="flex gap-2">
        <button
          ref={seoPrevRef}
          type="button"
          className="seo-swiper-prev-2 flex items-center justify-center"
          style={{
            borderRadius: '18px',
            border: '1px solid #E4E9F0',
            background: '#FFF',
            boxShadow: '0px 68px 19px 0px rgba(0, 9, 56, 0.00), 0px 43px 17px 0px rgba(0, 9, 56, 0.00), 0px 24px 15px 0px rgba(0, 9, 56, 0.01), 0px 11px 11px 0px rgba(0, 9, 56, 0.02), 0px 3px 6px 0px rgba(0, 9, 56, 0.02)',
            width: '40px',
            height: '40px',
            padding: 0,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          ref={seoNextRef}
          type="button"
          className="seo-swiper-next-2 flex items-center justify-center"
          style={{
            borderRadius: '18px',
            border: '1px solid #E4E9F0',
            background: '#FFF',
            boxShadow: '0px 68px 19px 0px rgba(0, 9, 56, 0.00), 0px 43px 17px 0px rgba(0, 9, 56, 0.00), 0px 24px 15px 0px rgba(0, 9, 56, 0.01), 0px 11px 11px 0px rgba(0, 9, 56, 0.02), 0px 3px 6px 0px rgba(0, 9, 56, 0.02)',
            width: '40px',
            height: '40px',
            padding: 0,
            zIndex: 10000000,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div> */}

          <Swiper
            className='container mt-6'
            modules={[Navigation, Autoplay]}
            autoplay={{
              delay: 3000, // Adjusted delay for smoother experience
              disableOnInteraction: false,
            }}
            speed={1000} // Adjusted speed for smoother transitions
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            // navigation={{
            //   prevEl: seoPrevRef.current,
            //   nextEl: seoNextRef.current,
            // }}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 24,
              },
            }}
            // onBeforeInit={(swiper) => {
            //   // Ensure navigation is set before initialization
            //   // @ts-ignore
            //   swiper.params.navigation.prevEl = seoPrevRef.current;
            //   // @ts-ignore
            //   swiper.params.navigation.nextEl = seoNextRef.current;
            // }}
            onInit={(swiper) => {
              swiper.navigation.init();
              swiper.navigation.update();
            }}
          >
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#E0ECFB] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#206AC7] md:text-[22px]'>
                  Technical SEO Audit
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Every great SEO strategy begins with a solid foundation. Our
                  technical SEO audit dives deep into your website's
                  infrastructure to identify and fix performance bottlenecks. We
                  analyze page speed, crawl errors, indexing issues, mobile
                  responsiveness, and overall site health. This ensures your
                  site is not only accessible to users but also fully optimized
                  for search engine crawlers. With the help of advanced search
                  engine optimization tools, we deliver a comprehensive
                  technical report that highlights action points and
                  improvements.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#F9F7E4] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#8F7500] md:text-[22px]'>
                  Tag Optimization
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Meta tags, title tags, header tags, and alt attributes may
                  seem small—but they carry massive SEO value. We fine-tune
                  these elements to align with your target search engine
                  optimization keywords and user intent. Effective tag
                  optimization boosts your on-page SEO and ensures your content
                  is both readable and discoverable by search engines like
                  Google. The result? Higher click-through rates and better
                  visibility across your web pages.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#F9E4F9] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#206AC7] md:text-[22px]'>
                  Keyword Research
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  We conduct in-depth search engine optimization keyword
                  research to discover the exact terms your target audience is
                  using. Our approach balances high-search-volume keywords with
                  niche, long-tail phrases to help you dominate both competitive
                  and targeted spaces. Using powerful SEO tools and trend
                  analysis, we develop a keyword strategy that guides every
                  piece of your website content, driving traffic that converts.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[400px] rounded-[24px] bg-[#E4F9E7] p-[24px]'>
                <p className='mb-6 font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-[#238A31] md:text-[22px]'>
                  Content Structure Optimization
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#373844]'>
                  Search engines love well-structured content—and so do users.
                  We optimize your content layout to ensure logical flow, use of
                  headers (H1, H2, etc.), keyword placement, and internal
                  linking. This improves readability, increases engagement, and
                  supports better indexing. Whether it's service pages, blog
                  posts, or landing pages, our focus is on creating a structure
                  that satisfies both human users and search engine optimizers.
                </p>
              </div>
            </SwiperSlide>
          </Swiper>

          <div className='absolute right-0 top-0 z-30 h-full w-[80px] bg-[linear-gradient(270deg,_#FFF_0%,_rgba(255,255,255,0)_100%)] md:w-[200px]'></div>
        </div>
      </section>

      <section className='px-3 py-20  md:container md:px-0 md:py-[100px]'>
        <h2 className='mb-6 text-center font-NeueMontreal text-[32px] font-normal leading-[120%] text-[#000] md:text-[40px]'>
          Email Marketing
        </h2>
        <p className='mx-auto mb-12 max-w-2xl text-center font-Schibsted_Grotesk text-[16px] font-normal leading-[140%] text-[#373844] md:text-[18px]'>
          Email marketing remains one of the most effective channels to connect
          with your audience directly. By delivering personalized messages
          straight to their inboxes, you can nurture leads, build lasting
          relationships, and drive conversions.
        </p>
        {/* Desk view    Email Marketing start */}

        <div className='hidden grid-cols-1 gap-[23px] md:grid md:grid-cols-2 lg:grid-cols-3'>
          <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
              Pay-Per-Click (PPC) Advertising
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We create targeted PPC campaigns that place your brand at the
              forefront of search engine results. By bidding on relevant
              keywords and optimizing ad copy, we drive high-quality traffic to
              your website, maximizing your return on investment.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
              Search Engine Marketing (SEM)
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Our SEM strategies combine paid advertising and optimization
              techniques to increase your online visibility. Through continuous
              analysis and adjustment, we ensure your ads reach the right
              audience at the right time.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
              Conversion Rate Optimization (CRO)
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We analyze user behavior to identify and eliminate barriers in
              your conversion funnel. By optimizing landing pages and
              call-to-action elements, we enhance user experience, leading to
              increased conversions and sales.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
              Display Advertising
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We design visually appealing display ads that capture attention
              across various websites and platforms. By targeting specific
              audiences, we increase brand visibility and drive traffic to your
              site.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
              Remarketing Campaigns
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              To re-engage visitors who didn't convert initially, we implement
              retargeting strategies that remind them of your offerings. These
              campaigns are tailored to encourage return visits and complete
              desired actions.
            </p>
          </div>
        </div>
        {/* Desk view    Email Marketing ENd */}

        {/* Mobile view    Email Marketing start */}

        <div className='block md:hidden'>
          <style>{customStyles}</style>
          <Swiper
            className='swiper container relative mt-6'
            modules={[Navigation, Autoplay, FreeMode]}
            autoplay={{
              delay: 2500, // Increased delay for smoother transitions
              disableOnInteraction: false,
            }}
            speed={1000} // Reduced speed for better control
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            aria-label="Previous slide"
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            centeredSlides={true}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
          >
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
                  Pay-Per-Click (PPC) Advertising
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We create targeted PPC campaigns that place your brand at the
                  forefront of search engine results. By bidding on relevant
                  keywords and optimizing ad copy, we drive high-quality traffic
                  to your website, maximizing your return on investment.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
                  Search Engine Marketing (SEM)
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Our SEM strategies combine paid advertising and optimization
                  techniques to increase your online visibility. Through
                  continuous analysis and adjustment, we ensure your ads reach
                  the right audience at the right time.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
                  Conversion Rate Optimization (CRO)
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We analyze user behavior to identify and eliminate barriers in
                  your conversion funnel. By optimizing landing pages and
                  call-to-action elements, we enhance user experience, leading
                  to increased conversions and sales.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
                  Display Advertising
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We design visually appealing display ads that capture
                  attention across various websites and platforms. By targeting
                  specific audiences, we increase brand visibility and drive
                  traffic to your site.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#FAF0CB] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#8F7500]'>
                  Remarketing Campaigns
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  To re-engage visitors who didn't convert initially, we
                  implement retargeting strategies that remind them of your
                  offerings. These campaigns are tailored to encourage return
                  visits and complete desired actions.
                </p>
              </div>
            </SwiperSlide>
            <div className='mt-7 flex h-[44px] items-center justify-center gap-2 md:hidden'>
              <div
                ref={emailPrevRef}
                className='swiper-button-prev'
                aria-label='Previous slide'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M12.5 15L7.5 10L12.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div
                ref={emailNextRef}
                className='swiper-button-next'
                aria-label='Next slide'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 15L12.5 10L7.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            {/* Navigation Buttons */}
          </Swiper>
        </div>

        {/* Mobile view    Email Marketing ENd */}
      </section>

      <div className='bg-[#F5F8FF]'>
        <section className='px-3 py-20 md:container md:px-0 md:py-[100px]'>
          <h2 className='mb-6 text-center font-NeueMontreal text-[32px] font-normal not-italic leading-[120%] text-[#090D28] md:text-start md:text-[40px]'>
            Social Media Marketing
          </h2>
          <div className='p-0 md:p-6'>
            <div className='flex flex-col gap-3 md:flex-row md:gap-6'>
              {/* Sidebar (Headings) */}
              <div className='flex w-full  flex-col gap-3 md:w-1/2'>
                {headings.map((heading) => (
                  <Fragment key={heading.id}>
                    <div
                      className={`flex cursor-pointer  flex-col gap-2.5 self-stretch p-6 
                ${activeHeading === heading.id ? 'rounded-2xl border-l-4 border-[#0051BB] bg-[#E0ECFB] md:block' : 'rounded-[24px] bg-white md:block '}`}
                      onClick={() => handleHeadingClick(heading.id)}
                    >
                      <div className=''>
                        <p
                          className={`m-0   whitespace-nowrap font-Schibsted_Grotesk text-[18px] font-semibold not-italic leading-[120%] md:text-[20px]
                  ${activeHeading === heading.id ? 'text-[#0051BB]' : 'text-[#090D28]'}`}
                        >
                          {heading.title}
                        </p>
                      </div>
                      <div className='w-1/2 '>
                        {/* Content shown below active heading in mobile view */}
                      </div>
                    </div>

                    {activeHeading === heading.id && (
                      <div className='rounded-[24px]  !bg-[#0051BB] p-6  text-base font-normal leading-[150%] md:hidden'>
                        <p className='mb-4  font-Schibsted_Grotesk text-[20px] font-bold not-italic leading-[120%] text-white'>
                          {heading.title}
                        </p>
                        <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#E4EEFB]'>
                          {heading.content}
                        </p>
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>

              {/* Right Content Area (Desktop Only) */}
              <div className='bg-content-bg text-content-text font-latinka-regular hidden w-1/2  flex-1 rounded-[24px] !bg-[#0051BB] p-6 text-base font-normal leading-[150%] md:block'>
                <p className='mb-4  font-Schibsted_Grotesk text-[28px] font-bold not-italic leading-[120%] text-white'>
                  {activeHeadingData?.title}
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal not-italic leading-[150%] text-[#E4EEFB]'>
                  {activeHeadingData?.content}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className='px-3 py-20 md:container md:px-0 md:py-[100px]'>
        <h2 className='mb-6 text-center font-NeueMontreal text-[32px] font-normal leading-[120%] text-[#000] md:text-[40px]'>
          Performance Marketing
        </h2>
        <p className='mx-auto mb-12 max-w-2xl text-center font-Schibsted_Grotesk text-[16px] font-normal leading-[140%] text-[#373844] md:text-[18px]'>
          Performance marketing is all about results. Whether it’s clicks,
          leads, or sales you only pay when your goals are met. With smart
          strategies and real-time tracking, we help businesses grow faster by
          reaching the right audience at the right time.
        </p>
        {/* Desk view    Email Marketing start */}

        <div className='hidden grid-cols-1 gap-[23px] md:grid md:grid-cols-2 lg:grid-cols-3'>
          <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#287900]'>
              Pay-Per-Click (PPC) Advertising
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We create targeted PPC campaigns that place your brand at the
              forefront of search engine results. By bidding on relevant
              keywords and optimizing ad copy, we drive high-quality traffic to
              your website, maximizing your return on investment.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#287900]'>
              Search Engine Marketing (SEM)
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Our SEM strategies combine paid advertising and optimization
              techniques to increase your online visibility. Through continuous
              analysis and adjustment, we ensure your ads reach the right
              audience at the right time.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#287900]'>
              Conversion Rate Optimization (CRO)
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We analyze user behavior to identify and eliminate barriers in
              your conversion funnel. By optimizing landing pages and
              call-to-action elements, we enhance user experience, leading to
              increased conversions and sales.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#287900]'>
              Display Advertising
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We design visually appealing display ads that capture attention
              across various websites and platforms. By targeting specific
              audiences, we increase brand visibility and drive traffic to your
              site.
            </p>
          </div>
          <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#287900]'>
              Remarketing Campaigns
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              To re-engage visitors who didn't convert initially, we implement
              retargeting strategies that remind them of your offerings. These
              campaigns are tailored to encourage return visits and complete
              desired actions.
            </p>
          </div>
        </div>
        {/* Desk view    Email Marketing ENd */}

        {/* Mobile view    Email Marketing start */}

        <div className='block md:hidden'>
          <style>{customStyles}</style>
          <Swiper
            className='swiper container relative mt-6'
            modules={[Navigation, Autoplay, FreeMode]}
            autoplay={{
              delay: 2500, // Increased delay for smoother transitions
              disableOnInteraction: false,
            }}
            speed={1000} // Reduced speed for better control
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            aria-label="Previous slide"
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            centeredSlides={true}
            breakpoints={{
              768: {
                slidesPerView: 1,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 1,
                spaceBetween: 24,
              },
            }}
          >
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#4F9E28]'>
                  Pay-Per-Click (PPC) Advertising
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We create targeted PPC campaigns that place your brand at the
                  forefront of search engine results. By bidding on relevant
                  keywords and optimizing ad copy, we drive high-quality traffic
                  to your website, maximizing your return on investment.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#4F9E28]'>
                  Search Engine Marketing (SEM)
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Our SEM strategies combine paid advertising and optimization
                  techniques to increase your online visibility. Through
                  continuous analysis and adjustment, we ensure your ads reach
                  the right audience at the right time.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#4F9E28]'>
                  Conversion Rate Optimization (CRO)
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We analyze user behavior to identify and eliminate barriers in
                  your conversion funnel. By optimizing landing pages and
                  call-to-action elements, we enhance user experience, leading
                  to increased conversions and sales.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#4F9E28]'>
                  Display Advertising
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We design visually appealing display ads that capture
                  attention across various websites and platforms. By targeting
                  specific audiences, we increase brand visibility and drive
                  traffic to your site.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='rounded-[24px] bg-[#E2FFD3] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[22px] font-semibold leading-[120%] text-[#4F9E28]'>
                  Remarketing Campaigns
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  To re-engage visitors who didn't convert initially, we
                  implement retargeting strategies that remind them of your
                  offerings. These campaigns are tailored to encourage return
                  visits and complete desired actions.
                </p>
              </div>
            </SwiperSlide>
            <div className='mt-7 flex h-[44px] items-center justify-center gap-2 md:hidden'>
              <div className='swiper-button-prev' aria-label='Previous slide'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M12.5 15L7.5 10L12.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='swiper-button-next' aria-label='Next slide'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 15L12.5 10L7.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        </div>
      </section>

      <section className='bg-[#01001E]'>
        <section className='px-3 py-[60px] md:container md:p-0 md:py-[120px] '>
          <h2 className='text-center cfont-NeueMontreal text-[32px] font-extrabold leading-[120%] text-white md:text-[48px]'>
            We Make for Your Team{' '}
          </h2>
          <p className='py-4  text-center font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-white md:text-[18px]'>
            Our team ensures timely results without compromising quality
          </p>

          <section className='flex items-center justify-center gap-3 '>
            <TestiReviewCommonImages />
            <div>
              <a
                href='https://maps.app.goo.gl/wxZr7ckDhrWnVbx27'
                className='text-[#090D28]'
                target='_blank'
              >
                {' '}
                <p className=' m-0 flex items-center gap-1 p-0 pb-[6px] text-center font-LatinkaRegular text-sm font-normal leading-[120%] text-white'>
                  <Image
                    className='w-6 h-auto'
                    src={getImageUrl("/vilvabusiness/website/common/homepage/google-img.webp")}
                    alt='GoogleImg'
                    width={24}
                    height={24}
                  />{' '}
                  Google Reviews
                </p>
              </a>
              <div className='flex items-center gap-2 text-white'>
                4.7
                <div className='flex items-center gap-2'>
                  <SvgIcons.ReviewStar />
                  <SvgIcons.ReviewStar />
                  <SvgIcons.ReviewStar />
                  <SvgIcons.ReviewStar />
                  <SvgIcons.ReviewStar />
                </div>
              </div>
            </div>
          </section>

          <section className='pt-[20px] md:container md:pt-[30px]'>
            <StatsSection />
          </section>
        </section>
      </section>

      <section className='px-3 py-[60px] md:container md:p-0 md:py-[100px] '>
        <h2 className='mb-6 text-center font-NeueMontreal text-[32px] font-normal leading-[120%] text-[#000] md:text-[40px]'>
          Marketing Automation
        </h2>
        <p className='mx-auto mb-12 max-w-2xl text-center font-Schibsted_Grotesk text-[16px] font-normal leading-[140%] text-[#373844] md:text-[18px]'>
          Connect Smarter with WhatsApp Marketing. With Vilva Chat by
          VilvaBusiness, we automate personalized, timely messages on WhatsApp
          helping you engage customers, boost responses, and drive real results
          effortlessly.
        </p>

        <div className='hidden grid-cols-1 gap-6 px-3 md:grid md:grid-cols-2 lg:grid-cols-4'>
          <div className='h-full rounded-[24px] bg-[#F1ECFA] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
              Personalized Campaigns
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We craft tailored messages that resonate with your audience,
              incorporating elements like emojis, images, and videos to enhance
              engagement. By analyzing customer behavior and preferences, we
              ensure each message feels personal and relevant.
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#F1ECFA] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
              Automated Workflows
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Our automation strategies allow you to send messages triggered by
              specific customer actions, such as sign-ups or purchases. This
              ensures timely communication, nurturing leads through the sales
              funnel efficiently
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#F1ECFA] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
              Segmentation and Targeting
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We segment your audience based on demographics, purchase history,
              and engagement levels. This targeted approach ensures that your
              messages reach the right people, increasing the likelihood of
              conversions.
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#F1ECFA] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
              Analytics and Reporting
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Analyse the performance of your campaigns with detailed analytics.
              Monitor total templates created, messages processed, queue stats,
              and team metrics. Enjoy complete visibility into every element of
              your WhatsApp marketing campaigns.
            </p>
          </div>
        </div>
        <div className='block md:hidden'>
          <style>{customStyles}</style>
          <Swiper
            className='swiper container relative mt-6'
            modules={[Navigation, Autoplay, FreeMode]}
            autoplay={{
              delay: 2500, // Increased delay for smoother transitions
              disableOnInteraction: false,
            }}
            speed={1000} // Reduced speed for better control
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            centeredSlides={true}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
          >
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#F1ECFA] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
                  Personalized Campaigns
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We craft tailored messages that resonate with your audience,
                  incorporating elements like emojis, images, and videos to
                  enhance engagement. By analyzing customer behavior and
                  preferences, we ensure each message feels personal and
                  relevant.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#F1ECFA] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
                  Automated Workflows
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Our automation strategies allow you to send messages triggered
                  by specific customer actions, such as sign-ups or purchases.
                  This ensures timely communication, nurturing leads through the
                  sales funnel efficiently
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#F1ECFA] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
                  Segmentation and Targeting
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We segment your audience based on demographics, purchase
                  history, and engagement levels. This targeted approach ensures
                  that your messages reach the right people, increasing the
                  likelihood of conversions.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#F1ECFA] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#5A2AB2]'>
                  Analytics and Reporting
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Analyse the performance of your campaigns with detailed
                  analytics. Monitor total templates created, messages
                  processed, queue stats, and team metrics. Enjoy complete
                  visibility into every element of your WhatsApp marketing
                  campaigns.
                </p>
              </div>
            </SwiperSlide>
            <div className='mt-7 flex h-[44px] items-center justify-center gap-2 md:hidden'>
              <div className='swiper-button-prev' aria-label='Previous slide'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M12.5 15L7.5 10L12.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='swiper-button-next' aria-label='Next slide'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 15L12.5 10L7.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            {/* Navigation Buttons */}
          </Swiper>
        </div>
      </section>

      <section className='px-3 py-[60px] md:container md:p-0 md:py-[100px] '>
        <h2 className='mb-6 text-center font-NeueMontreal text-[32px] font-normal leading-[120%] text-[#000] md:text-[40px]'>
          Meta & Google ADS
        </h2>
        <p className='mx-auto mb-12 max-w-2xl text-center font-Schibsted_Grotesk text-[16px] font-normal leading-[140%] text-[#373844] md:text-[18px]'>
          Connect with the right people at the right time. Meta & Google Ads
          help you boost visibility, generate quality leads, and drive real
          conversions all with measurable results and smart spending.
        </p>

        <div className='hidden grid-cols-1 gap-6 px-3 md:grid md:grid-cols-2 lg:grid-cols-4'>
          <div className='h-full rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#8F7500]'>
              Personalized Campaigns
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Every successful ad campaign starts with a plan. We begin by
              understanding your business goals and target audience. Then, we
              craft a tailored ad strategy for Meta (Facebook, Instagram) and
              Google platforms that aligns
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#8F7500]'>
              Detailed Reporting & Insights
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Transparency is key. We provide clear, detailed reports on ad
              performance highlighting metrics like reach, click-through rate
              (CTR), conversions, and cost-per-result. These insights help you
              measure ROI and make informed decisions moving forward.
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#8F7500]'>
              Keyword & Audience Targeting
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              Using detailed keyword research and audience insights, we target
              users based on demographics, interests, behaviors, and search
              intent. This helps maximize your Google Ads performance and
              improves Meta Ads relevance, ensuring every penny is spent wisely.
            </p>
          </div>
          <div className='h-full rounded-[24px] bg-[#FAF0CB] p-[24px]'>
            <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#8F7500]'>
              Ad Creation & Optimization
            </p>
            <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
              We design eye-catching ad creatives with compelling copy that
              resonates with your audience. From carousel ads on Instagram to
              responsive search ads on Google, we ensure each campaign is
              visually appealing, conversion focused, and optimized for
              engagement.
            </p>
          </div>
        </div>
        <div className='block md:hidden'>
          <style>{customStyles}</style>
          <Swiper
            className='swiper container relative mt-6'
            modules={[Navigation, Autoplay, FreeMode]}
            autoplay={{
              delay: 2500, // Increased delay for smoother transitions
              disableOnInteraction: false,
            }}
            speed={1000} // Reduced speed for better control
            loop={true}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            centeredSlides={true}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 24,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
          >
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#FAF5DF] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#89741A]'>
                  Campaign Strategy & Planning
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Every successful ad campaign starts with a plan. We begin by
                  understanding your business goals and target audience. Then,
                  we craft a tailored ad strategy for Meta (Facebook, Instagram)
                  and Google platforms that aligns
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#FAF5DF] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#89741A]'>
                  Detailed Reporting & Insights
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Transparency is key. We provide clear, detailed reports on ad
                  performance highlighting metrics like reach, click-through
                  rate (CTR), conversions, and cost-per-result. These insights
                  help you measure ROI and make informed decisions moving
                  forward.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#FAF5DF] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#89741A]'>
                  Keyword & Audience Targeting
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  Using detailed keyword research and audience insights, we
                  target users based on demographics, interests, behaviors, and
                  search intent. This helps maximize your . Google Ads
                  performance and improves Meta Ads relevance, ensuring every
                  penny is spent wisely.
                </p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className='h-[300px] rounded-[24px] bg-[#FAF5DF] p-[24px]'>
                <p className='mb-4 font-Schibsted_Grotesk text-[20px] font-semibold leading-[120%] text-[#89741A]'>
                  Ad Creation & Optimization
                </p>
                <p className='font-Schibsted_Grotesk text-[16px] font-normal leading-[150%] text-[#373844]'>
                  We design eye-catching ad creatives with compelling copy that
                  resonates with your audience. From carousel ads on Instagram
                  to responsive search ads on Google, we ensure each campaign is
                  visually appealing, conversion focused, and optimized for
                  engagement.
                </p>
              </div>
            </SwiperSlide>
            <div className='mt-7 flex h-[44px] items-center justify-center gap-2 md:hidden'>
              <div className='swiper-button-prev' >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M12.5 15L7.5 10L12.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='swiper-button-next' >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M7.5 15L12.5 10L7.5 5'
                    stroke='black'
                    strokeWidth='1.25'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            {/* Navigation Buttons */}
          </Swiper>
        </div>
      </section>

      <PricingSection />

      <section className='cont md:px-0amd:iner px-3 pb-20 md:pb-[120px]'>
        <FaqSection faqs={faqs} />
      </section>
    </>
  );
};

export default DigitalMarketingView;


